import { getSasTokenSignature, uploadSignatureWithSas } from "views/v2/services/uploadService";
import { toast, Notification } from "components/ui";

/**
 * Compress and convert image to base64 data URL with max length constraint
 * @param {File} file - Image file to compress
 * @param {number} maxLength - Maximum length of the data URL string (default: 87000 for ~64KB with base64 overhead)
 * @param {number} availableSpace - Available space in the total field (for fitting within overall limit)
 * @returns {Promise<{success: boolean, dataUrl?: string, error?: string}>}
 */
export const compressImageToDataUrl = async (file, maxLength = 45000, availableSpace = null) => {
  
  const effectiveMaxLength = availableSpace !== null 
    ? Math.min(maxLength, Math.max(availableSpace, 100)) 
    : maxLength;
  return new Promise((resolve) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        
        // Start with original dimensions
        let width = img.width;
        let height = img.height;
        const aspectRatio = width / height;
        
        // Initial max dimensions - start conservative
        let maxWidth = Math.min(width, 1200);
        let maxHeight = Math.min(height, 800);
        
        // Adjust to maintain aspect ratio
        if (width > 1200) {
          maxWidth = 1200;
          maxHeight = Math.round(maxWidth / aspectRatio);
        }
        if (maxHeight > 800) {
          maxHeight = 800;
          maxWidth = Math.round(maxHeight * aspectRatio);
        }
        
        // Function to compress with given dimensions and quality
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Try multiple quality levels and dimension reductions
        const qualities = [0.9, 0.8, 0.7, 0.6, 0.5, 0.4, 0.3, 0.2, 0.1];
        let bestResult = null;
        let bestLength = Infinity;
        
        for (let q of qualities) {
          // Reset dimensions for each quality level
          let currentW = maxWidth;
          let currentH = maxHeight;
          
          // Try up to 5 dimension reductions per quality
          for (let attempt = 0; attempt < 5; attempt++) {
            canvas.width = currentW;
            canvas.height = currentH;
            ctx.clearRect(0, 0, currentW, currentH);
            ctx.drawImage(img, 0, 0, currentW, currentH);
            
            const dataUrl = canvas.toDataURL('image/jpeg', q);
            
            if (dataUrl.length <= effectiveMaxLength) {
              // Found a valid compression
              resolve({
                success: true,
                dataUrl: dataUrl,
              });
              return;
            }
            
            // Track best result
            if (dataUrl.length < bestLength) {
              bestLength = dataUrl.length;
              bestResult = dataUrl;
            }
            
            // Reduce dimensions for next attempt
            if (attempt < 4) {
              currentW = Math.floor(currentW * 0.8);
              currentH = Math.floor(currentH * 0.8);
              
              // Prevent too small images
              if (currentW < 50 || currentH < 50) {
                break;
              }
            }
          }
        }
        
        // If we got here, compression failed
        if (bestResult && bestResult.length <= effectiveMaxLength) {
          resolve({
            success: true,
            dataUrl: bestResult,
          });
        } else {
          let errorMsg = 'Image is too large even after compression.';
          
          if (availableSpace !== null && availableSpace < 1000) {
            errorMsg = `Not enough space in signature field (${availableSpace.toLocaleString()} characters available). Total limit is 1500 characters. Please remove some content first.`;
          } else if (availableSpace !== null) {
            errorMsg = `Image is too large for available space (${availableSpace.toLocaleString()} characters available). Please use a smaller image or reduce dimensions.`;
          } else {
            // Calculate approximate KB from maxLength (base64 adds ~33% overhead)
            // actual size ≈ maxLength * 3/4 bytes
            const approximateKB = Math.round((effectiveMaxLength * 3) / 4 / 1024);
            errorMsg = `Image is too large even after compression. Maximum compressed size is approximately ${approximateKB}KB. Please use a smaller image.`;
          }
          
          resolve({
            success: false,
            error: errorMsg,
          });
        }
      };
      
      img.onerror = () => {
        resolve({
          success: false,
          error: 'Failed to load image for compression.',
        });
      };
      
      img.src = e.target.result;
    };
    
    reader.onerror = () => {
      resolve({
        success: false,
        error: 'Failed to read image file.',
      });
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * Get Azure SAS token for file upload
 * @param {string} fileName - Name of the file
 * @returns {Promise<{success: boolean, sasUrl?: string, blobName?: string, error?: any}>}
 */
export const getAzureSasToken = async (fileName) => {
  try {
    // Only send filename in request body
    const requestBody = {
      fileName,
    };

    // Get SAS token
    const response = await getSasTokenSignature(requestBody);

    if (response.data.success) {
      const { sasUrl, blobName } = response.data.data;
      return {
        success: true,
        sasUrl,
        blobName,
      };
    } else {
      throw new Error('Failed to get SAS token');
    }
  } catch (error) {
    console.error('Error getting SAS token:', error);
    return {
      success: false,
      error: error?.message || 'Failed to get SAS token',
    };
  }
};

/**
 * Upload image or video to Azure Blob Storage
 * @param {File} file - The file to upload
 * @param {string} type - File type identifier (e.g., 'signature', 'profile') - used for filename prefix only
 * @returns {Promise<{success: boolean, url?: string, path?: string, error?: any}>}
 */
export const uploadMediaToAzure = async (file, type = 'signature') => {
  try {
    // Generate unique filename with timestamp
    const timestamp = Date.now();
    const fileExtension = file.name.split('.').pop();
    const fileName = `${type}_${timestamp}.${fileExtension}`;

    // Get SAS token - only send filename
    const sasResult = await getAzureSasToken(fileName);

    if (!sasResult.success) {
      throw new Error(sasResult.error || 'Failed to get SAS token');
    }

    const { sasUrl, blobName } = sasResult;

    // Ensure SAS URL is used as-is (don't modify or encode it)
    // The URL from backend should already be properly formatted
    const uploadUrl = sasUrl;

    // Upload file to Azure using signature-specific upload method
    await uploadSignatureWithSas(file, uploadUrl);

    // Get the blob URL (without SAS token for permanent URL)
    const blobUrl = sasUrl.split('?')[0];

    return {
      success: true,
      url: blobUrl,
      path: blobName,
    };
  } catch (error) {
    console.error('Error uploading media to Azure:', error);
    toast.push(
      <Notification title="Upload Failed" type="danger" closable>
        {error?.message || 'Failed to upload image/video. Please try again.'}
      </Notification>
    );
    return {
      success: false,
      error: error?.message || 'Upload failed',
    };
  }
};

/**
 * Create a file input element and trigger file selection
 * @param {string} accept - File types to accept (e.g., 'image/*', 'video/*')
 * @returns {Promise<File|null>}
 */
export const selectMediaFile = (accept = 'image/*,video/*') => {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', accept);
    input.style.display = 'none';

    input.onchange = (event) => {
      const file = event.target.files?.[0];
      resolve(file || null);
      document.body.removeChild(input);
    };

    input.oncancel = () => {
      resolve(null);
      document.body.removeChild(input);
    };

    document.body.appendChild(input);
    input.click();
  });
};
